import { normalizeCategory as normalizeCategoryConfig } from "../config/categoriesConfig";
import { calculateSalePrice } from "../config/marginConfig";

const ONE_API_URL = "https://apiv1-production-2f9d.up.railway.app";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const pickString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

const normalizeCategory = (rawCategory) => {
  // Primero extraemos el string de la categoría cruda
  let categoryString = "Sin categoria";
  
  if (typeof rawCategory === "string") {
    categoryString = rawCategory;
  } else if (rawCategory && typeof rawCategory === "object") {
    categoryString = pickString(rawCategory.nombre, rawCategory.name, rawCategory.descripcion);
  }
  
  // Luego normalizamos usando el mapeo de categorías de onelectronics.com.ar
  return normalizeCategoryConfig(categoryString);
};

const normalizeBrand = (rawBrand) => {
  if (typeof rawBrand === "string") {
    return rawBrand;
  }
  if (rawBrand && typeof rawBrand === "object") {
    return pickString(rawBrand.nombre, rawBrand.name);
  }
  return "One Electronics";
};

const normalizeGoogleDriveImage = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmed = url.trim();
  const fileMatch = trimmed.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  const openMatch = trimmed.match(/[?&]id=([^&]+)/i);
  const driveId = fileMatch?.[1] || openMatch?.[1];
  if (driveId) {
    // Tamaño moderado por defecto; el componente <Item> puede pedir variantes
    // mediante getDriveImageVariants() para servir srcset responsivo.
    return `https://drive.google.com/thumbnail?id=${driveId}&sz=w800`;
  }

  return trimmed;
};

// Helper para generar variantes responsivas a partir de cualquier URL de
// thumbnail de Google Drive. Devuelve { src, srcSet } listo para usar en
// <img>. Si la URL no es de Drive devuelve { src: url, srcSet: "" }.
export const getDriveImageVariants = (url) => {
  if (!url || typeof url !== "string") return { src: "", srcSet: "" };
  const idMatch = url.match(/[?&]id=([^&]+)/i);
  const driveId = idMatch?.[1];
  if (!driveId) return { src: url, srcSet: "" };
  const base = `https://drive.google.com/thumbnail?id=${driveId}`;
  return {
    src: `${base}&sz=w800`,
    srcSet: `${base}&sz=w400 400w, ${base}&sz=w800 800w, ${base}&sz=w1200 1200w`,
  };
};

const normalizeImage = (rawProduct) => {
  const images = rawProduct?.imagenes || rawProduct?.images;
  if (Array.isArray(images) && images.length) {
    const principal = images.find((img) => img?.principal);
    const candidate = principal || images[0];
    if (typeof candidate === "string") {
      return normalizeGoogleDriveImage(candidate);
    }
    return normalizeGoogleDriveImage(pickString(candidate?.url, candidate?.src, candidate?.imagen));
  }

  const fallback = pickString(
    rawProduct?.imagen,
    rawProduct?.image,
    rawProduct?.foto,
    rawProduct?.thumbnail
  );

  return normalizeGoogleDriveImage(fallback);
};

const extractItems = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.items)) {
    return payload.items;
  }
  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

const normalizeProduct = (rawProduct) => {
  const sourceIdValue = rawProduct?.id ?? rawProduct?.codigo ?? rawProduct?.sku ?? rawProduct?.productoId;
  if (sourceIdValue === undefined || sourceIdValue === null || sourceIdValue === "") {
    return null;
  }

  const sourceId = String(sourceIdValue);
  const productCode = pickString(rawProduct?.codigo, rawProduct?.sku, sourceId);
  const stock = toNumber(rawProduct?.stock ?? rawProduct?.cantidad ?? rawProduct?.existencias ?? 0, 0);
  
  // Precio del mayorista
  const costPrice = toNumber(rawProduct?.pre_particular ?? rawProduct?.precio ?? rawProduct?.price ?? 0, 0);
  
  // Calcular precio de venta con margen automático
  const priceData = calculateSalePrice(costPrice);

  return {
    id: sourceId,
    sourceId,
    code: productCode,
    name: pickString(rawProduct?.nombre, rawProduct?.name, rawProduct?.titulo) || "Producto sin nombre",
    description: pickString(rawProduct?.descripcion, rawProduct?.description, rawProduct?.detalle) || "Sin descripcion disponible",
    category: normalizeCategory(rawProduct?.categoria),
    brand: normalizeBrand(rawProduct?.marca),
    // Precios con margen
    costPrice: priceData.costPrice,
    price: priceData.salePrice,
    salePrice: priceData.salePrice,
    margin: priceData.margin,
    marginPercent: priceData.marginPercent,
    profit: priceData.profit,
    marginTier: priceData.tier.name,
    stock,
    image: normalizeImage(rawProduct),
    availability: stock > 0 ? "active" : "out_of_stock",
    isActive: stock > 0,
  };
};

export const fetchOneElectronicsProducts = async ({ limit = 200, maxPages = 8 } = {}) => {
  const uniqueById = new Map();

  for (let page = 1; page <= maxPages; page++) {
    const response = await fetch(`${ONE_API_URL}/productos?inStock=false&includeHidden=true&limit=${limit}&page=${page}`);
    if (!response.ok) {
      throw new Error(`No se pudo cargar el catalogo (${response.status})`);
    }

    const payload = await response.json();
    const items = extractItems(payload);

    if (!items.length) {
      break;
    }

    for (const rawProduct of items) {
      const normalized = normalizeProduct(rawProduct);
      if (normalized) {
        uniqueById.set(normalized.id, normalized);
      }
    }

    if (items.length < limit) {
      break;
    }
  }

  return [...uniqueById.values()];
};

export const fetchOneElectronicsProductById = async (productId) => {
  try {
    const response = await fetch(`${ONE_API_URL}/productos/${encodeURIComponent(productId)}`);
    if (response.ok) {
      const payload = await response.json();
      const raw = payload?.data || payload;
      const normalized = normalizeProduct(raw);
      if (normalized) {
        return normalized;
      }
    }
  } catch (error) {
    console.warn("No se pudo obtener detalle directo, se intenta fallback:", error);
  }

  const products = await fetchOneElectronicsProducts({ maxPages: 8 });
  return products.find((item) => String(item.id) === String(productId)) || null;
};
